import React, { useCallback, useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppScreen } from '../../components/AppScreen';
import { ErrorState } from '../../components/ErrorState';
import { ScreenHeader } from '../../components/ScreenHeader';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { listOrganizations } from '../../services/managedOrganizationService';
import type { ManagedOrganization } from '../../types/managedOrganization';
import { normalizeApiError } from '../../api/apiError';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'OrganizationList'>;

export function OrganizationListScreen({ navigation }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<ManagedOrganization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems((await listOrganizations(search)).items);
      setError(null);
    } catch (caught) {
      setError(normalizeApiError(caught).message);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useFocusEffect(useCallback(() => { load().catch(() => undefined); }, [load]));
  if (!auth.roles?.includes('SUPER_ADMIN')) return <UnauthorizedScreen />;

  return (
    <AppScreen scroll>
      <ScreenHeader title="Organizations" onBack={navigation.goBack} />
      <View style={styles.body}>
        <AppInput label="Search" value={search} onChangeText={setSearch}
          placeholder="Organization code or name" autoCapitalize="none" />
        <View style={styles.actions}>
          <AppButton title="Search" variant="secondary" onPress={() => load()} style={styles.flex} />
          <AppButton title="Create Organization"
            onPress={() => navigation.navigate(routes.organizationCreate)} style={styles.flex} />
        </View>
        {loading ? <ActivityIndicator color={palette.primary} /> : null}
        {error ? <ErrorState message={error} onRetry={load} /> : null}
        {!loading && !error && items.length === 0 ? (
          <Text style={[styles.empty, { color: palette.textSecondary }]}>No organizations found.</Text>
        ) : null}
        {items.map(item => (
          <Pressable key={item.organizationId} accessibilityRole="button"
            accessibilityLabel={`Open ${item.organizationName}`}
            onPress={() => navigation.navigate(routes.managedOrganizationDetails,
              { organizationId: item.organizationId })}>
            <AppCard style={styles.card}>
              <Text style={[styles.name, { color: palette.textPrimary }]}>{item.organizationName}</Text>
              <Text style={[styles.meta, { color: palette.textSecondary }]}>{item.organizationCode}</Text>
              <Text style={[styles.meta, { color: palette.textSecondary }]}>
                {item.isActive ? 'Active' : 'Inactive'}
              </Text>
            </AppCard>
          </Pressable>
        ))}
      </View>
    </AppScreen>
  );
}

const styles = StyleSheet.create({
  body: { padding: spacing.lg, gap: spacing.md },
  actions: { flexDirection: 'row', gap: spacing.sm },
  flex: { flex: 1 },
  card: { gap: spacing.xs },
  name: { ...typography.body, fontWeight: '700' },
  meta: { ...typography.caption },
  empty: { ...typography.body, textAlign: 'center', padding: spacing.xl },
});
